const BASE_URL = process.env.REACT_APP_COIPortal_Url;

export interface CoverageEntry {
  PolicyNumber: string;
  NaicId: string | null;
  EffectiveDate: string;      // "YYYY-MM-DD HH:mm:ss"
  ExpirationDate: string;     // "YYYY-MM-DD HH:mm:ss"
  AcordFormNumber: number;
  CoveragesAndLimits: string; // XML string
  SignatureId: string | null;
}

export interface CreateMasterCertificatePayload {
  databaseId?: string;
  TruckingCompanyId: string;
  AcordFormNumber: number;
  Name: string;
  UseInHoaUnit: boolean;
  PolicyIds: string[];
  BuildingIds: string[];
  Coverages: CoverageEntry[];
  BlanketAdditionalInterestTypes: number[];
  BlanketWaiverofSubrogation: number[];
}

/**
 * Maps BLANKET_OPTIONS `value` strings to their API bitmask integers.
 * Matches the order used in AddMastercertificate BLANKET_OPTIONS.
 */
export const BLANKET_VALUE_MAP: Record<string, number> = {
  general_liability:   1,
  auto_liability:      2,
  umbrella_liability:  4,
  other_liability_1:   8,
  other_liability_2:   16,
  workers_compensation: 32,
};

/**
 * Maps ACORD_FORMS registry keys to the parent-level AcordFormNumber
 * sent in the create-master-certificate payload.
 */
export const ACORD_FORM_NUMBERS: Record<string, number> = {
  acord20:     20,
  acord21:     21,
  acord22:     22,
  acord23:     23,
  acord24:         24,
  acord24_v2016:   2416,
  acord24_101: 24101,
  acord25:          25,
  acord25_101:      25101,
  acord25_v2016:    2516,
  acord25_v2016_101: 16101,
  acord27:          27,
  acord27_v2016:    2716,
  acord27_101:      27101,
  acord28:          28,
  acord28_v2016:    2816,
  acord28_101:      28101,
  acord29:     29,
  acord30:     30,
  acord75:     75,
};

export interface EditMasterCertificatePayload {
  certificateId: string;
  TruckingCompanyId: string;
  AcordFormNumber: number;
  Name: string;
  UseInHoaUnit: boolean;
  PolicyIds: string[];
  BuildingIds: string[];
  Coverages: CoverageEntry[];
  BlanketAdditionalInterestTypes: number[];
  BlanketWaiverofSubrogation: number[];
}

export async function createMasterCertificate(
  payload: CreateMasterCertificatePayload,
): Promise<void> {
  const ncToken = localStorage.getItem('NCToken') ?? '';
  const igToken = localStorage.getItem('igtoken') ?? '';

  const res = await fetch(`${BASE_URL}/api/IGIGCOI/create-master-certificate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      NCToken: ncToken,
      Authorization: `Bearer ${igToken}`,
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`create-master-certificate failed [${res.status}]: ${text}`);
  }
}

export async function fetchMasterCertificateCoverages(masterCertId: string): Promise<any[]> {
  const ncToken = localStorage.getItem('NCToken') ?? '';
  const igToken = localStorage.getItem('igtoken') ?? '';

  const res = await fetch(`${BASE_URL}/api/igigcoi/master-certificate-coverages/${encodeURIComponent(masterCertId)}`, {
    headers: {
      NCToken: ncToken,
      Authorization: `Bearer ${igToken}`,
    },
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`master-certificate-coverages failed [${res.status}]: ${text}`);
  }

  const data = await res.json();
  return Array.isArray(data) ? data : (Array.isArray(data?.value) ? data.value : []);
}

export async function fetchBuildingCoverages(buildingId: string, acordFormNumber: number): Promise<any[]> {
  const ncToken = localStorage.getItem('NCToken') ?? '';
  const igToken = localStorage.getItem('igtoken') ?? '';

  const res = await fetch(`${BASE_URL}/api/igigcoi/building-coverages`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      NCToken: ncToken,
      Authorization: `Bearer ${igToken}`,
    },
    body: JSON.stringify({ buildingId, acordFormNumber }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`building-coverages failed [${res.status}]: ${text}`);
  }

  return res.json();
}

export async function archiveMasterCertificate(
  certificateId: string,
  insuredDatabaseId: string,
  active: boolean = false,
  certificateName: string = '',
): Promise<void> {
  const ncToken = localStorage.getItem('NCToken') ?? '';
  const igToken = localStorage.getItem('igtoken') ?? '';

  const res = await fetch(`${BASE_URL}/api/IGIGCOI/archive-master-certificate`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      NCToken: ncToken,
      Authorization: `Bearer ${igToken}`,
    },
    body: JSON.stringify({ certificateId, insuredDatabaseId, active, certificateName }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`archive-master-certificate failed [${res.status}]: ${text}`);
  }
}

export async function editMasterCertificate(
  payload: EditMasterCertificatePayload,
): Promise<void> {
  const ncToken = localStorage.getItem('NCToken') ?? '';
  const igToken = localStorage.getItem('igtoken') ?? '';

  const res = await fetch(`${BASE_URL}/api/IGIGCOI/edit-master-certificate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      NCToken: ncToken,
      Authorization: `Bearer ${igToken}`,
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`edit-master-certificate failed [${res.status}]: ${text}`);
  }
}


/**
 * Tracker #257: creates a carrier from a name and NAIC typed on a certificate form.
 * Same endpoint the Add New Carrier dialog uses; the AMS scopes the record to the
 * caller's agency. Resolves to the new carrier's databaseId.
 */
export async function insertCarrier(name: string, naicCode: string): Promise<string> {
  const ncToken = localStorage.getItem('NCToken') ?? '';
  const igToken = localStorage.getItem('igtoken') ?? '';

  const res = await fetch(`${BASE_URL}/api/igigcoi/carrier/insert`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      NCToken: ncToken,
      Authorization: `Bearer ${igToken}`,
    },
    body: JSON.stringify({ name: name.trim(), naicCode: naicCode.trim() || null, active: true }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`carrier/insert failed [${res.status}]: ${text}`);
  }
  const json: any = await res.json().catch(() => ({}));
  const id = json?.databaseId ?? json?.DatabaseId ?? '';
  if (!id) throw new Error(`carrier/insert returned no databaseId for "${name}"`);
  return String(id);
}
